import { Link } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";

import {
  FiActivity,
  FiArrowUpRight,
  FiCalendar,
  FiCheckCircle,
  FiChevronDown,
  FiHeart,
  FiMonitor,
  FiShield,
  FiSmartphone,
  FiUsers,
  FiDatabase,
  FiCpu,
} from "react-icons/fi";

import "../styles/Healthcare.css";

const images = {
  hero:
    "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1800&q=85",

  patient:
    "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?auto=format&fit=crop&w=1000&q=85",

  platform:
    "https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=1000&q=85",

  mobile:
    "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?auto=format&fit=crop&w=1000&q=85",

  operations:
    "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=1000&q=85",

  approach:
    "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1400&q=85",

  closing:
    "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1800&q=85",
};

const solutions = [
  {
    icon: <FiUsers />,
    title: "Patient connection",
    text: "Create useful, timely communication that keeps patients informed before, during, and after care.",
    image: images.patient,
    tag: "PATIENT FIRST",
  },
  {
    icon: <FiMonitor />,
    title: "Healthcare web platforms",
    text: "Accessible websites and portals that make services, specialists, and next steps simple to find.",
    image: images.platform,
    tag: "DIGITAL ACCESS",
  },
  {
    icon: <FiSmartphone />,
    title: "Mobile care journeys",
    text: "Bring appointments, reminders, resources, and support together in a purposeful mobile experience.",
    image: images.mobile,
    tag: "MOBILE CARE",
  },
  {
    icon: <FiActivity />,
    title: "Intelligent operations",
    text: "Use automation and actionable data to reduce manual work and help teams focus on people.",
    image: images.operations,
    tag: "SMART OPERATIONS",
  },
];

const services = [
  [
    "Patient experience",
    "Clearer digital paths from first search to follow-up care.",
    <FiUsers />,
  ],
  [
    "Care websites",
    "Fast, accessible websites built for trust and easy navigation.",
    <FiMonitor />,
  ],
  [
    "Appointment journeys",
    "Smoother booking, reminders, and communication workflows.",
    <FiCalendar />,
  ],
  [
    "Secure platforms",
    "Protected, scalable systems designed around your operational needs.",
    <FiShield />,
  ],
  [
    "Healthcare growth",
    "Relevant campaigns and content that help the right people find you.",
    <FiActivity />,
  ],
  [
    "Insight & automation",
    "Practical technology that helps teams act faster and more confidently.",
    <FiDatabase />,
  ],
];

const approachItems = [
  [
    "01",
    "Understand",
    "Listen to patients, teams, workflows, and care goals.",
  ],
  [
    "02",
    "Prioritize",
    "Focus on the changes that create the clearest value.",
  ],
  [
    "03",
    "Create",
    "Design and build secure, approachable digital experiences.",
  ],
  [
    "04",
    "Improve",
    "Learn from what is live and keep making care easier.",
  ],
];

const faqItems = [
  [
    "What healthcare technology can Techlynx help us build?",
    "We design and develop websites, patient portals, mobile apps, care-management workflows, dashboards, and connected digital platforms.",
  ],
  [
    "Can you improve our existing healthcare software?",
    "Yes. We can review existing experiences, improve interfaces and workflows, and connect the tools your teams already use.",
  ],
  [
    "How do you approach patient data and security?",
    "We design with secure practices, appropriate access controls, and your organization's compliance requirements in mind.",
  ],
  [
    "Can you integrate third-party systems?",
    "Yes. We plan integrations carefully so your platforms, data, and teams can work together more smoothly.",
  ],
  [
    "Can you create online appointment booking systems?",
    "Yes. We can build simple, accessible booking journeys with confirmations, reminders, rescheduling, and integration with your existing systems.",
  ],
  [
    "Can you build patient portals?",
    "Yes. We create secure portals where patients can view information, manage appointments, access documents, and communicate with care teams.",
  ],
  [
    "Can the healthcare platform work on mobile devices?",
    "Yes. We design responsive websites and mobile experiences so patients and staff can access important services from any device.",
  ],
  [
    "Do you provide support after launch?",
    "Yes. We can monitor, improve, maintain, and extend the platform as patient and operational needs change.",
  ],
];

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;

    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          element.classList.add("health-visible");
          observer.unobserve(element);
        }
      },
      {
        threshold: 0.12,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`health-reveal ${className}`}
      style={{ "--health-delay": `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function HealthcareVisual() {
  return (
    <div className="health-visual" aria-hidden="true">
      <div className="health-visual-glow" />

      <div className="health-ring ring-one" />
      <div className="health-ring ring-two" />
      <div className="health-ring ring-three" />

      <div className="health-orbit-dot dot-one" />
      <div className="health-orbit-dot dot-two" />
      <div className="health-orbit-dot dot-three" />

      <div className="health-device">
        <div className="device-top">
          <span>HEALTH / 01</span>
          <FiActivity />
        </div>

        <div className="device-heart">
          <FiHeart />
        </div>

        <div className="device-wave">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>

        <div className="device-status">
          <i />
          SYSTEM CONNECTED
        </div>
      </div>

      <div className="health-badge badge-one">
        <FiShield />
      </div>

      <div className="health-badge badge-two">
        <FiCalendar />
      </div>

      <div className="health-badge badge-three">
        <FiCpu />
      </div>

      <div className="health-pulse">
        <FiActivity />
        <span>LIVE</span>
      </div>
    </div>
  );
}

function Healthcare() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <main className="health-page">

      {/* HERO */}
      <section
        className="health-hero"
        style={{ "--health-hero-image": `url("${images.hero}")` }}
      >
        <div className="health-hero-image" />
        <div className="health-hero-overlay" />

        <div className="health-grid-lines" />

        <div className="health-hero-inner">
          <Reveal className="health-hero-copy">
            <span className="health-label">
              Techlynx for Healthcare
            </span>

            <h1>
              Care experiences that feel{" "}
              <em>more human</em>
            </h1>

            <p>
              We help healthcare organizations connect people, information,
              and technology—making care easier to access and simpler to
              manage.
            </p>

            <a
              href="#health-solutions"
              className="health-button"
            >
              Explore healthcare solutions
              <FiArrowUpRight />
            </a>

            <div className="health-hero-meta">
              <span>
                <i />
                DIGITAL HEALTH
              </span>

              <span>
                <i />
                HUMAN-CENTRED
              </span>

              <span>
                <i />
                CONNECTED CARE
              </span>
            </div>
          </Reveal>

          <Reveal className="health-visual-wrap" delay={180}>
            <HealthcareVisual />
          </Reveal>
        </div>

        <div className="health-scroll">
          <span />
          SCROLL TO EXPLORE
        </div>
      </section>

      {/* SOLUTIONS */}
      <section
        id="health-solutions"
        className="health-solutions"
      >
        <div className="health-container">

          <div className="health-heading">
            <Reveal>
              <span className="health-label">
                Healthcare, connected
              </span>

              <h2>
                Technology that supports{" "}
                <em>better care</em>
              </h2>
            </Reveal>

            <Reveal delay={150}>
              <p>
                Modern healthcare needs clarity, trust, and reliable tools.
                We build experiences that are useful for patients and
                practical for the teams who serve them.
              </p>
            </Reveal>
          </div>

          <div className="health-solution-grid">
            {solutions.map((item, index) => (
              <Reveal
                key={item.title}
                delay={index * 100}
              >
                <article className="health-solution-card">

                  <div className="solution-image">
                    <img
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                    />

                    <div className="solution-image-overlay" />

                    <span>{item.tag}</span>
                  </div>

                  <div className="solution-card-content">

                    <div className="solution-top">
                      <div className="solution-icon">
                        {item.icon}
                      </div>

                      <small>
                        0{index + 1}
                      </small>
                    </div>

                    <h3>{item.title}</h3>

                    <p>{item.text}</p>

                    <div className="solution-line" />
                  </div>

                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="health-services">
        <div className="health-container">

          <Reveal>
            <div className="health-heading-single">
              <span className="health-label">
                Digital health, designed well
              </span>

              <h2>
                Everything your organization needs to{" "}
                <em>move forward</em>
              </h2>
            </div>
          </Reveal>

          <div className="health-service-layout">

            <Reveal className="health-service-image">
              <div className="service-image-frame">
                <img
                  src={images.approach}
                  alt="Healthcare professionals working together"
                  loading="lazy"
                />

                <div className="service-image-overlay" />

                <div className="service-image-caption">
                  <span>TECHLYNX / HEALTHCARE</span>
                  <strong>
                    Connected systems.
                    <br />
                    Better experiences.
                  </strong>
                </div>
              </div>
            </Reveal>

            <div className="health-service-grid">
              {services.map(([title, text, icon], index) => (
                <Reveal
                  key={title}
                  delay={index * 70}
                >
                  <article className="health-service-card">

                    <div className="service-number">
                      0{index + 1}
                    </div>

                    <div className="service-icon">
                      {icon}
                    </div>

                    <h3>{title}</h3>

                    <p>{text}</p>

                    <span className="service-arrow">
                      <FiArrowUpRight />
                    </span>

                  </article>
                </Reveal>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="health-approach">

        <div className="health-approach-bg">
          <div className="approach-glow" />
        </div>

        <div className="health-container approach-container">

          <Reveal className="approach-copy">
            <span className="health-label">
              How we work
            </span>

            <h2>
              A thoughtful path from{" "}
              <em>need to impact</em>
            </h2>

            <p>
              We start with the reality of your patients, staff, and
              systems. Then we shape a digital roadmap that brings the
              right improvements together over time.
            </p>

            <div className="approach-stat">
              <strong>04</strong>
              <span>
                STEPS TO
                <br />
                DIGITAL IMPACT
              </span>
            </div>
          </Reveal>

          <div className="approach-list">
            {approachItems.map(([num, title, text], index) => (
              <Reveal
                key={num}
                delay={index * 110}
              >
                <article>

                  <b>{num}</b>

                  <div className="approach-content">
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>

                  <FiCheckCircle />

                  <div className="approach-progress">
                    <span />
                  </div>

                </article>
              </Reveal>
            ))}
          </div>

        </div>
      </section>

      {/* FAQ */}
      <section className="health-faq">

        <div className="health-container faq-container">

          <div className="faq-intro">

            <Reveal>
              <span className="health-label">
                Frequently asked questions
              </span>

              <h2>
                Questions deserve{" "}
                <em>clear answers</em>
              </h2>

              <p>
                Talk with Techlynx about the digital health experience
                you want to improve next.
              </p>

              <Link
                to="/contact"
                className="health-button"
              >
                Start a conversation
                <FiArrowUpRight />
              </Link>
            </Reveal>

            <Reveal className="faq-visual" delay={150}>
              <img
                src={images.patient}
                alt="Patient and healthcare professional"
                loading="lazy"
              />

              <div className="faq-visual-overlay" />

              <div className="faq-visual-content">
                <span>CARE / CONNECTED</span>

                <strong>
                  Technology should
                  <br />
                  feel human.
                </strong>
              </div>
            </Reveal>

          </div>

          <div className="faq-list">

            {faqItems.map(([question, answer], index) => {
              const isOpen = openFaq === index;

              return (
                <Reveal
                  key={question}
                  delay={index * 50}
                >
                  <article
                    className={
                      isOpen
                        ? "faq-item is-open"
                        : "faq-item"
                    }
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenFaq(
                          isOpen ? -1 : index
                        )
                      }
                      aria-expanded={isOpen}
                    >
                      <span>
                        <small>
                          0{index + 1}
                        </small>

                        {question}
                      </span>

                      <i>
                        <FiChevronDown />
                      </i>
                    </button>

                    <div
                      className={
                        isOpen
                          ? "faq-answer visible"
                          : "faq-answer"
                      }
                    >
                      {isOpen && (
                        <p>{answer}</p>
                      )}
                    </div>
                  </article>
                </Reveal>
              );
            })}

          </div>

        </div>
      </section>

      {/* CLOSING */}
      <section
        className="health-closing"
        style={{
          "--health-closing-image": `url("${images.closing}")`,
        }}
      >
        <div className="health-closing-overlay" />

        <div className="health-container closing-container">

          <Reveal className="closing-copy">

            <span className="health-label">
              Build better healthcare experiences
            </span>

            <h2>
              Turn healthcare challenges into{" "}
              <em>digital opportunity</em>
            </h2>

            <p>
              Whether you need a new healthcare platform, patient portal,
              mobile experience, connected systems, or a clear digital
              roadmap, Techlynx can help turn your vision into a scalable
              product.
            </p>

            <Link
              to="/contact"
              className="health-button"
            >
              Start a project
              <FiArrowUpRight />
            </Link>

          </Reveal>

          <Reveal className="closing-orbit" delay={200}>
            <div className="closing-orbit-ring" />
            <div className="closing-icon">
              <FiHeart />
            </div>
          </Reveal>

        </div>
      </section>

    </main>
  );
}

export default Healthcare;